import React, { useRef } from 'react';

import { GlobalCompanyTabCompanyType } from '../../../globalCompanyTabsTypes';

import {
  useCreateGlobalCompanyTabForm,
  CreateGlobalCompanyTabFormData
} from './hooks/useCreateGlobalCompanyTabForm';

import { useCurrentUser } from '../../../../../auth/hooks/useAuth';
import { useTranslate } from '../../../../../common/hooks/useTranslate';

import { RolesMultiSelectField } from '../../../../roles/helpers/RolesMultiSelectField';

import { AlertMessage } from '../../../../../helpers/AlertMessage';
import { CheckPermissions } from '../../../../../helpers/CheckPermissions';
import { Form } from '../../../../../helpers/Form';
import { InputField } from '../../../../../helpers/FormFields/InputField';
import { SimpleModalButtons } from '../../../../../helpers/modals/SimpleModal/components/SimpleModalButtons';

import { TeamsPermissions } from '../../../../teams/teamsConstants';

import { formsFields, words } from '../../../../../locales/keys';

interface CreateGlobalCompanyTabFormProps {
  form: string;
  companyType: GlobalCompanyTabCompanyType;
  hideModal: () => void;
}

function CreateGlobalCompanyTabForm({
  form,
  companyType,
  hideModal
}: CreateGlobalCompanyTabFormProps) {
  const { t } = useTranslate();
  const submitButtonRef = useRef<HTMLButtonElement | null>(null);
  const currentUser = useCurrentUser();

  const {
    control,
    errors,
    registerName,
    handleSubmit,
    createGlobalCompanyTabLoading,
    createGlobalCompanyTabErrorMessage
  } = useCreateGlobalCompanyTabForm({ companyType, hideModal });

  return (
    <>
      <div className="flex-1 overflow-y-auto px-2 text-sm">
        <Form id={form} className="p-4 space-y-4">
          {createGlobalCompanyTabErrorMessage && (
            <AlertMessage message={createGlobalCompanyTabErrorMessage} />
          )}

          <InputField
            error={
              errors.name ? errors.name.message || t(words.required) : null
            }
            disabled={createGlobalCompanyTabLoading}
            inputWrapperClassName="mt-1 relative"
            i18nLabel={formsFields.name}
            labelClassName="block text-sm font-medium text-gray-700 dark:text-gray-300"
            name={registerName.name}
            onChange={registerName.onChange}
            ref={registerName.ref}
            required
          />

          <CheckPermissions
            action={
              TeamsPermissions.READ_CREATE_GLOBAL_COMPANY_TAB_FORM_ACCESS_FIELD
            }
          >
            <RolesMultiSelectField<CreateGlobalCompanyTabFormData>
              control={control}
              disabled={
                createGlobalCompanyTabLoading ||
                !currentUser.hasPermissions(
                  TeamsPermissions.CHANGE_CREATE_GLOBAL_COMPANY_TAB_FORM_ACCESS_FIELD
                )
              }
              name="roleIds"
              i18nLabel={formsFields.access}
              i18nPlaceholder={formsFields.access}
              labelClassName="block text-sm font-medium text-gray-700 dark:text-gray-300"
              inputWrapperClassName="mt-1 relative"
            />
          </CheckPermissions>
        </Form>
      </div>

      <SimpleModalButtons
        handleSubmit={handleSubmit}
        hideModal={hideModal}
        i18nCancelText={words.cancel}
        i18nSubmitText={words.create}
        isLoading={createGlobalCompanyTabLoading}
        ref={submitButtonRef}
        submitButtonColor="blue"
      />
    </>
  );
}

export default CreateGlobalCompanyTabForm;
